import { GENERATOR_VERSION, generateDaily, generateLevel, upgradeExitRules } from './generator';
import { generateDaily as generateLegacyDaily, generateLevel as generateLegacyLevel } from './legacyGenerator';
import { publishedLevel, type Catalog } from './levelCatalog';
import { decodeLevel, encodeLevel, type EncodedLevel } from './levelCodec';
import { THEME_NAMES, type Attempt, type GameConfig, type Level, type Mode, type Player, type ThemeName } from './types';

export const SAVE_VERSION = 2;

/** An attempt as saved: its board snapshot in the compact form (levelCodec.ts). */
export type SavedAttempt = Omit<Attempt, 'levelSnapshot'> & { levelSnapshot?: EncodedLevel };

/** What the game saves: on the device (Preferences) and on the server (state.save). */
export type SaveData = { version: number; player: Player; attempts: Partial<Record<Mode, SavedAttempt>> };

export interface ProgressDeps {
  /** Wall clock in ms, corrected by the server time. */
  now(): number;
  /** A new random id for an attempt. */
  newId(): string;
  /** Called after every change with the data to save. */
  onChange(save: SaveData): void;
}

/** The UTC day of `now`, e.g. "2026-11-04": the Daily Challenge key. */
export function utcDay(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

export function newPlayer(config: GameConfig, now: number): Player {
  return {
    coins: config.startingCoins,
    completedIds: [], creditedRewards: [], unlockedThemes: ['Default'], theme: 'Default',
    dailyDate: utcDay(now), dailyAttempts: 0, dailyCompleted: false
  };
}

/** Reads untrusted saved data; anything unexpected starts a fresh player. */
export function readSave(data: unknown, config: GameConfig, now: number): { player: Player; attempts: Partial<Record<Mode, Attempt>> } {
  const fresh = { player: newPlayer(config, now), attempts: {} };
  if (typeof data !== 'object' || data === null) return fresh;
  const { version, player, attempts } = data as Partial<SaveData>;
  if (version !== SAVE_VERSION || !isPlayer(player)) return fresh;
  const kept: Partial<Record<Mode, Attempt>> = {};
  if (attempts && typeof attempts === 'object') {
    for (const mode of ['regular', 'daily'] as const) {
      const attempt = attempts[mode];
      if (!isAttempt(attempt, mode)) continue;
      // A snapshot that does not decode drops the attempt: its board is unknown.
      try {
        const { levelSnapshot, ...rest } = attempt;
        kept[mode] = levelSnapshot ? { ...rest, levelSnapshot: decodeLevel(levelSnapshot) } : rest;
      } catch { /* the player starts that board again */ }
    }
  }
  return { player, attempts: kept };
}

function encodeAttempt(attempt: Attempt): SavedAttempt {
  const { levelSnapshot, ...rest } = attempt;
  return levelSnapshot ? { ...rest, levelSnapshot: encodeLevel(levelSnapshot) } : rest;
}

function isPlayer(value: unknown): value is Player {
  const p = value as Partial<Player> | undefined;
  return !!p && typeof p === 'object' &&
    Number.isSafeInteger(p.coins) && (p.coins as number) >= 0 &&
    isStrings(p.completedIds) && isStrings(p.creditedRewards) &&
    Array.isArray(p.unlockedThemes) && p.unlockedThemes.every(t => THEME_NAMES.includes(t)) &&
    THEME_NAMES.includes(p.theme as ThemeName) &&
    typeof p.dailyDate === 'string' && Number.isSafeInteger(p.dailyAttempts) && typeof p.dailyCompleted === 'boolean' &&
    (p.tutorialStatus === undefined || p.tutorialStatus === 'completed' || p.tutorialStatus === 'skipped');
}

function isAttempt(value: unknown, mode: Mode): value is SavedAttempt {
  const a = value as Partial<SavedAttempt> | undefined;
  return !!a && typeof a === 'object' && a.mode === mode &&
    typeof a.id === 'string' && typeof a.levelId === 'string' && Number.isSafeInteger(a.seed) &&
    isStrings(a.remainingArrowIds) && Number.isSafeInteger(a.lives) && Number.isFinite(a.deadline) &&
    Number.isFinite(a.timeLimit) && typeof a.reviveUsed === 'boolean' && (a.status === 'playing' || a.status === 'failed');
}

function isStrings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(v => typeof v === 'string');
}

/**
 * The player's progress: coins, completed levels, themes, Daily Challenge and the
 * in-flight attempts. Every change calls `deps.onChange` with the data to save.
 */
export class Progress {
  private player: Player;
  private attempts: Partial<Record<Mode, Attempt>>;

  constructor(
    saved: unknown,
    public config: GameConfig,
    private levels: Catalog | undefined,
    private readonly deps: ProgressDeps
  ) {
    ({ player: this.player, attempts: this.attempts } = readSave(saved, config, deps.now()));
    this.rollDaily();
    this.upgradeSavedRules();
    this.retuneSavedTimers();
    this.save();
  }
  /** New tunables apply from the next action; in-flight attempts keep their timer and lives. */
  setConfig(config: GameConfig): void { this.config = config; }
  /** New published levels apply to new attempts; in-flight attempts keep their snapshot. */
  setLevels(levels: Catalog | undefined): void { this.levels = levels; }
  today(): string { return utcDay(this.deps.now()); }
  toSave(): SaveData {
    const attempts: SaveData['attempts'] = {};
    for (const mode of ['regular', 'daily'] as const) {
      const attempt = this.attempts[mode];
      if (attempt) attempts[mode] = encodeAttempt(attempt);
    }
    return { version: SAVE_VERSION, player: structuredClone(this.player), attempts };
  }
  private upgradeSavedRules(): void {
    const now = this.deps.now();
    for (const mode of ['regular', 'daily'] as const) {
      const attempt = this.attempts[mode];
      if (!attempt || (attempt.generatorVersion ?? 1) >= 5) continue;
      const original = this.getLevel(mode, mode === 'regular' ? Number(attempt.levelId.replace('level-', '')) : undefined);
      const updated = upgradeExitRules(original);
      if (updated === original) continue;
      attempt.generatorVersion = updated.generatorVersion;
      attempt.levelSnapshot = structuredClone(updated);
      // Completed boards stay complete. An incompatible in-flight layout must
      // restart because its old arrow IDs no longer describe the same paths.
      if (attempt.remainingArrowIds.length) {
        attempt.remainingArrowIds = updated.arrows.map(arrow => arrow.id);
        attempt.rulesUpdated = true;
        if (attempt.status === 'playing' && attempt.deadline > now) {
          attempt.deadline = Math.max(attempt.deadline, now + updated.timeLimit * 1000);
          attempt.lives = this.config.lives;
          attempt.mistakes = 0;
        }
      }
      attempt.timeLimit = updated.timeLimit;
    }
  }
  private retuneSavedTimers(): void {
    const now = this.deps.now();
    for (const mode of ['regular', 'daily'] as const) {
      const attempt = this.attempts[mode];
      if (!attempt || ![2, 3, 5, GENERATOR_VERSION].includes(attempt.generatorVersion ?? 1)) continue;
      const level = this.getLevel(mode, mode === 'regular' ? Number(attempt.levelId.replace('level-', '')) : undefined);
      if (attempt.timeLimit === level.timeLimit) continue;
      // One-time pacing update: preserve the fraction of time left, board,
      // lives and attempt ID. Never revive an expired clock or shorten a paid revive.
      if (attempt.status === 'playing' && !attempt.reviveUsed && attempt.deadline > now) {
        const fraction = Math.min(1, (attempt.deadline - now) / (Math.max(1, attempt.timeLimit) * 1000));
        attempt.deadline = now + fraction * level.timeLimit * 1000;
      }
      attempt.timeLimit = level.timeLimit;
    }
  }
  getPlayer(): Player { return { ...this.player, completedIds: [...this.player.completedIds], unlockedThemes: [...this.player.unlockedThemes] }; }
  getAttempt(mode: Mode): Attempt | null { const a = this.attempts[mode]; return a ? structuredClone(a) : null; }
  needsTutorial(): boolean {
    return !this.player.tutorialStatus && !this.player.completedIds.length && !this.player.creditedRewards.length &&
      !this.player.dailyAttempts && !this.player.dailyCompleted && !this.attempts.regular && !this.attempts.daily;
  }
  finishTutorial(status: 'completed' | 'skipped'): void {
    if (this.player.tutorialStatus !== 'completed') this.player.tutorialStatus = status;
    this.save();
  }
  nextLevel(): number {
    for (const i of this.config.progressionOrder) if (!this.player.completedIds.includes(`level-${i}`)) return i;
    return this.config.maxLevel + 1;
  }
  /** The board the current release has for `number`: published content first, else the built-in level. */
  currentLevel(number: number): Level {
    return publishedLevel(this.levels, number) ?? generateLevel(number);
  }
  getLevel(mode: Mode, number?: number): Level {
    this.rollDaily();
    const ordinal = number ?? this.nextLevel();
    const today = this.today();
    const id = mode === 'daily' ? `daily-${today}` : `level-${ordinal}`;
    const saved = this.attempts[mode];
    // Old saves predate the version field. Keep their original geometry and
    // seed on resume/retry; only new puzzles use the latest board dimensions.
    if (saved?.levelId === id) {
      if (saved.levelSnapshot) return structuredClone(saved.levelSnapshot);
      const version = saved.generatorVersion ?? 1;
      if (![1, 2, 3, 5, GENERATOR_VERSION].includes(version)) throw new Error(`Unsupported puzzle version: ${version}`);
      if (mode === 'daily') return version === 1 ? generateLegacyDaily(today) : generateDaily(today, version);
      return version === 1 ? generateLegacyLevel(ordinal, saved.seed) : generateLevel(ordinal, saved.seed, undefined, version);
    }
    return mode === 'daily' ? generateDaily(today) : this.currentLevel(ordinal);
  }
  rollDaily(): void {
    const today = this.today();
    if (this.player.dailyDate === today) return;
    this.player.dailyDate = today;
    // Past days' Daily rewards can never be claimed again: keep the save small.
    this.player.creditedRewards = this.player.creditedRewards.filter(id => !id.includes(':daily-') || id.endsWith(`:daily-${today}`));
    this.player.dailyAttempts = 0;
    this.player.dailyCompleted = false;
    delete this.attempts.daily;
    this.save();
  }
  canStartDaily(): boolean {
    this.rollDaily();
    return this.config.dailyChallenge && !this.player.dailyCompleted && this.player.dailyAttempts < this.config.dailyMaxAttempts;
  }
  begin(mode: Mode, level: Level): Attempt | null {
    this.rollDaily();
    if (mode === 'daily' && !this.canStartDaily()) return null;
    if (mode === 'daily') this.player.dailyAttempts++;
    const now = this.deps.now();
    this.attempts[mode] = {
      id: this.deps.newId(), mode, levelId: level.id, seed: level.seed, generatorVersion: level.generatorVersion,
      remainingArrowIds: level.arrows.map(a => a.id), lives: this.config.lives, mistakes: 0,
      timeLimit: level.timeLimit, deadline: now + level.timeLimit * 1000,
      reviveUsed: false, status: 'playing', startedAt: now, levelSnapshot: structuredClone(level)
    };
    this.save();
    return this.getAttempt(mode);
  }
  updateAttempt(attempt: Attempt): void { this.attempts[attempt.mode] = { ...attempt, remainingArrowIds: [...attempt.remainingArrowIds] }; this.save(); }
  finish(mode: Mode, reward: number): number {
    const attempt = this.attempts[mode];
    if (!attempt) return 0;
    const isFirst = attempt.mode === 'daily' ? !this.player.dailyCompleted : !this.player.completedIds.includes(attempt.levelId);
    if (attempt.mode === 'regular' && !this.player.completedIds.includes(attempt.levelId)) this.player.completedIds.push(attempt.levelId);
    if (attempt.mode === 'daily') this.player.dailyCompleted = true;
    // Daily level ids carry their date, so each day's reward is its own entitlement.
    const grant = isFirst ? this.award(reward, `reward:${attempt.levelId}`) : 0;
    delete this.attempts[mode];
    this.save();
    return grant;
  }
  award(amount: number, entitlement: string): number {
    if (this.player.creditedRewards.includes(entitlement)) return 0;
    const safeAmount = Math.max(0, Math.floor(amount));
    this.player.coins += safeAmount;
    this.player.creditedRewards.push(entitlement);
    this.save();
    return safeAmount;
  }
  spend(amount: number): boolean {
    if (!Number.isFinite(amount) || amount < 0 || this.player.coins < amount) return false;
    this.player.coins -= amount;
    this.save();
    return true;
  }
  theme(name: ThemeName): boolean {
    const cost = this.config.themePrices[name];
    if (!this.player.unlockedThemes.includes(name)) {
      if (!this.spend(cost)) return false;
      this.player.unlockedThemes.push(name);
    }
    this.player.theme = name;
    this.save();
    return true;
  }
  clearAttempt(mode: Mode): void { delete this.attempts[mode]; this.save(); }
  resetProgress(): void {
    this.player = newPlayer(this.config, this.deps.now());
    this.attempts = {};
    this.save();
  }
  private save(): void { this.deps.onChange(this.toSave()); }
}
